import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useLogin } from '../hooks/use-login'
import { Button } from './Button'
import { FormError } from './FormError'
import { Input } from './Input'
import { ROUTE_PATHS } from '@/config/app.config'

/**
 * Admin sign-in form (served at `/admin`), separate from the main `/login`
 * flow (SSO + email OTP + MFA). Email + password against `POST /admin/auth/login`
 * (see `useLogin`); lands on the role-based home page once the session is set —
 * a tenant-less account resolves to the AMLiQ super admin.
 */
export function LoginForm() {
  const navigate = useNavigate()
  const login = useLogin()
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')

  const canSubmit = email.trim().length > 0 && password.length > 0 && !login.isPending

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return

    login.mutate(
      { email: email.trim(), password },
      { onSuccess: () => navigate({ to: ROUTE_PATHS.root }) },
    )
  }

  return (
    <form className="flex flex-col gap-6" id="form-admin-login" onSubmit={handleSubmit} noValidate>
      <Input
        type="text"
        id="input-admin-username"
        label="Username"
        placeholder="you@company.com"
        autoComplete="username"
        value={email}
        disabled={login.isPending}
        hasError={login.isError}
        onChange={(event) => setEmail(event.target.value)}
      />

      <div className="flex flex-col gap-1.5">
        <Input
          type="password"
          id="input-admin-password"
          label="Password"
          placeholder="Your password"
          autoComplete="current-password"
          value={password}
          disabled={login.isPending}
          hasError={login.isError}
          onChange={(event) => setPassword(event.target.value)}
        />

        {login.isError && (
          <FormError id="alert-admin-login-error">
            {login.error.message || 'Your sign-in failed - please try again.'}
          </FormError>
        )}
      </div>

      <Button type="submit" id="btn-admin-login-submit" disabled={!canSubmit}>
        {login.isPending ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  )
}
